import { createRouter } from "./router.js";
import { bootSetupOrchestrator, restartTour } from "./ui/setupOrchestrator.js";
import { initHelpDrawer, toggleHelpDrawer } from "./ui/helpDrawer.js";
import { initNavBindings } from "./ui/navigation.js";
import { initCommandPalette } from "./ui/commandPalette.js";
import { escapeHtml } from "./ui/escapeHtml.js";
import { initAgentInboxBadge } from "./ui/agentInboxBadge.js";
import { openFeedbackModal } from "./ui/feedbackModal.js";
import { startBrowserNotifier } from "./ui/browserNotifier.js";
import { computeBrainScore } from "./utils/brainScore.js";

function requireEl(sel) {
  const el = document.querySelector(sel);
  if (!el) throw new Error("UI_CONTRACT_VIOLATION: missing " + sel);
  return el;
}

import { client } from "./api/client.js";
import { captureAttribution } from "./utils/attribution.js";
import { initClarity } from "./utils/clarity.js";

function updateTierBadge(user) {
    // Tier indicator now lives only in the profile dropdown (see
    // updatePopover). The sidebar workspace lockup keeps the brand
    // wordmark + period as the single visual focus — no more
    // AGENCY/PRO/TRIAL pill cluttering the logo.
    const b = document.getElementById('sidebar-workspace-plan');
    if (!b) return;
    b.textContent = '';
    b.className = 'sidebar-workspace-plan';
}

function updateAvatars(user) {
  const sidebarAv = document.getElementById('sidebar-avatar');
  const topbarAv = document.getElementById('topbar-avatar');
  const sidebarName = document.getElementById('sidebar-user-name');
  const sidebarRole = document.getElementById('sidebar-user-role');
  if (!user) return;
  const name = user.name || 'User';
  const initials = name.split(' ').map(w => w.charAt(0).toUpperCase()).join('').slice(0, 2) || 'U';
  const initial = name.charAt(0).toUpperCase();
  if (user.avatar_path) {
    if (sidebarAv) sidebarAv.innerHTML = `<img src="${escapeHtml(user.avatar_path)}" alt="${escapeHtml(initial)}">`;
    if (topbarAv) topbarAv.innerHTML = `<img src="${escapeHtml(user.avatar_path)}" alt="${escapeHtml(initials)}">`;
  } else {
    if (sidebarAv) sidebarAv.textContent = initial;
    if (topbarAv) topbarAv.textContent = initials;
  }
  if (topbarAv) topbarAv.title = name;
  // Update sidebar user name and role
  if (sidebarName) sidebarName.textContent = name;
  if (sidebarRole) {
    const roleLabels = { god_admin: 'Founder', admin: 'Admin', user: 'Member' };
    const tierLabels = { pro: 'Pro', starter: 'Starter', trial: 'Trial', free: 'Free' };
    const roleLabel = roleLabels[user.role] || 'Member';
    const tierLabel = tierLabels[user.tier] || '';
    sidebarRole.textContent = roleLabel + (tierLabel ? ` · ${tierLabel}` : '');
  }
}

function updatePopover(user) {
  const popoverName = document.getElementById('popover-name');
  const popoverEmail = document.getElementById('popover-email');
  const popoverAvatar = document.getElementById('popover-avatar');
  const popoverTier = document.getElementById('popover-tier-badge');
  const popoverThemeLabel = document.getElementById('popover-theme-label');

  if (popoverName) popoverName.textContent = user ? (user.name || 'User') : 'Guest';
  if (popoverEmail) popoverEmail.textContent = user ? (user.email || '') : '';

  if (popoverAvatar && user) {
    const name = user.name || 'User';
    const initials = name.split(' ').map(w => w.charAt(0).toUpperCase()).join('').slice(0, 2) || 'U';
    if (user.avatar_path) {
      popoverAvatar.innerHTML = `<img src="${escapeHtml(user.avatar_path)}" alt="${escapeHtml(initials)}">`;
    } else {
      popoverAvatar.textContent = initials;
    }
  }

  if (popoverTier && user) {
    // Always show the user's tier here (this is the only place the
    // tier indicator lives now that the sidebar lockup is logo-only).
    // For trial users, append the days-left countdown. Admin/founder
    // role gets stamped as a secondary chip below by separate logic.
    const role = user.role || 'user';
    const t = user.tier || 'trial';
    let label = t.toUpperCase();
    if (t === 'trial' && user.trial_expires_at && !user.has_stripe) {
      const d = Math.max(0, Math.ceil((new Date(user.trial_expires_at) - Date.now()) / 86400000));
      label = 'TRIAL \u00B7 ' + d + 'd left';
    } else if (role === 'god_admin') {
      // Founder gets a role tag prepended ('FOUNDER \u00B7 AGENCY').
      label = 'FOUNDER \u00B7 ' + t.toUpperCase();
    } else if (role === 'admin' || role === 'angel_admin') {
      label = 'ADMIN \u00B7 ' + t.toUpperCase();
    }
    popoverTier.textContent = label;
    popoverTier.className = 'account-popover__tier plan-badge plan-badge--' + t;
  }

  if (popoverThemeLabel) {
    const theme = window.__getTheme ? window.__getTheme() : 'dark';
    popoverThemeLabel.textContent = theme === 'dark' ? 'Switch to Light' : 'Switch to Dark';
  }
}

function updateImpersonationBanner(user) {
  const existing = document.getElementById('impersonation-banner');
  // Match the COOKIE VALUE, not just the key — when impersonation ends,
  // the server clears the cookie value to "" but the key may linger in
  // document.cookie. Without the value check we'd show the banner for
  // brand-new accounts that have never been impersonated.
  const cookieMatch = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('impersonating='));
  const cookieValue = cookieMatch ? cookieMatch.slice('impersonating='.length) : '';
  // Server-side /me also returns user.impersonating — trust whichever is
  // truthy. Cookie alone can be stale; user.impersonating is authoritative.
  const isImpersonating = !!cookieValue || !!(user && user.impersonating);
  if (isImpersonating && user) {
    if (!existing) {
      const banner = document.createElement('div');
      banner.id = 'impersonation-banner';
      banner.className = 'impersonation-banner';
      banner.innerHTML = `<span>Viewing as <strong>${escapeHtml(user.email)}</strong></span><button id="end-impersonation-btn" class="impersonation-banner__btn">Return to my account</button>`;
      document.body.prepend(banner);
      document.body.classList.add('has-impersonation-banner');
      banner.querySelector('#end-impersonation-btn').addEventListener('click', async () => {
        try { await client.adminEndImpersonation(); window.location.reload(); }
        catch (e) { alert('Failed to end impersonation: ' + e.message); }
      });
    }
  } else if (existing) {
    existing.remove();
    document.body.classList.remove('has-impersonation-banner');
  }
}

window.updateGlobalAuthState = (user, source = "unknown") => {
  console.log(`[AUTH_STATE] source=${source} user=${user ? user.email : "null"}`);
  window.__auth__ = user;
  const el = document.getElementById("user-display-name");
  if (el) el.textContent = user ? user.name : "Guest";
  updateTierBadge(user);
  updateAvatars(user);
  updatePopover(user);
  updateImpersonationBanner(user);
  // Tear down / re-evaluate the verify banner on every auth swap (login,
  // logout, impersonate). Without this, a stale "Please verify…" bar
  // lingers on the login page after logout.
  if (typeof renderVerifyBanner === 'function') renderVerifyBanner(user);
  const adminNav = document.getElementById('nav-admin');
  if (adminNav) adminNav.style.display = (['admin', 'angel_admin', 'god_admin'].includes(user?.role)) ? '' : 'none';
  // Agent Inbox discovery surfaces (sidebar link + topbar badge) react to tier changes.
  window.dispatchEvent(new CustomEvent('heist:auth-changed'));
};

document.addEventListener("DOMContentLoaded", async () => {
  // T-03 — snapshot utm_*/referrer/landing URL before any navigation can
  // rewrite the URL; read back by the signup page at register time.
  captureAttribution();

  const root = requireEl("#main-content");

  // Cache-staleness signal: the build stamps the bundle hash into a
  // <meta name="bundle-version"> tag. Compare it against the value we
  // persisted last load; if they differ, log a one-line console hint
  // so the user knows a hard-refresh will pick up newer code. We never
  // force a reload — just surface the divergence for diagnostics.
  try {
    const meta = document.querySelector('meta[name="bundle-version"]');
    const current = meta && meta.getAttribute('content');
    if (current) {
      const last = localStorage.getItem('heist:lastBundleVersion');
      if (last && last !== current) {
        console.warn(`[BOOT] new bundle version detected (was ${last}, now ${current}) — Ctrl+Shift+R if anything looks stale`);
      }
      localStorage.setItem('heist:lastBundleVersion', current);
    }
  } catch (_) { /* localStorage unavailable — ignore */ }

  // Sidebar + topbar Agent Inbox discovery widgets (no-op for free tier).
  initAgentInboxBadge();

  // 1. Initial Auth Check (Await this before starting router to avoid guard races)
  let bootUser = null;
  try {
    const { user } = await client.authMe();
    bootUser = user;
    window.updateGlobalAuthState(user, "BOOT");
    updateTierBadge(user);
    console.log("[BOOT] Authed as:", user.email);
  } catch (err) {
    window.updateGlobalAuthState(null, "BOOT_ERROR");
    console.log("[BOOT] Not authed:", err.message);
  }

  // T-05 — session replay (Clarity). Fire-and-forget: never blocks boot,
  // loads only when the server has a project id and the user isn't admin.
  fetch('/api/settings/public-config')
    .then(r => r.json())
    .then(cfg => initClarity(cfg.clarityProjectId, bootUser))
    .catch(() => {});

  // G7: Auto re-auth after Stripe payment success
  if (window.location.hash.includes('billing=success')) {
    console.log('[BILLING] Payment success — refreshing auth');
    for (let i = 0; i < 3; i++) {
      try {
        const { user } = await client.authMe();
        if (user.has_stripe || ['admin','angel_admin','god_admin'].includes(user.role)) {
          window.updateGlobalAuthState(user, 'BILLING_SUCCESS');
          updateTierBadge(user);
          window.location.hash = '#home';
          break;
        }
        await new Promise(r => setTimeout(r, 2000));
      } catch(e) {
        await new Promise(r => setTimeout(r, 2000));
      }
    }
  }

  // Auto-accept pending workspace invite after login
  const pendingInvite = sessionStorage.getItem('heist_pending_invite');
  if (pendingInvite && window.__auth__) {
    sessionStorage.removeItem('heist_pending_invite');
    try {
      await client.acceptInvite(pendingInvite);
      console.log('[BOOT] Auto-accepted workspace invite');
      window.location.hash = '#home';
      window.location.reload();
      return;
    } catch (e) {
      console.warn('[BOOT] Failed to auto-accept invite:', e.message);
    }
  }

  // Phase 24+: Load brands for multi-brand users
  try {
    const brandsRes = await client.getBrands();
    const brands = brandsRes.brands || [];
    const activeBrand = brandsRes.activeBrandId;
    localStorage.setItem('heist_active_brand', activeBrand);

    const user = window.__auth__;
    const tier = user?.tier || 'trial';
    const role = user?.role || 'user';
    const isMultiBrandTier = ['founder', 'agency', 'business'].includes(tier) || ['admin', 'angel_admin', 'god_admin'].includes(role);

    // Phase 34-B: workspace switcher rendered as a popover anchored to the
    // sidebar workspace lockup. The native <select id="brand-switcher"> stays
    // in the DOM hidden — kept as a screen-reader / no-JS fallback and so any
    // existing code that reads brand-switcher.value keeps working.
    const switcher = document.getElementById('brand-switcher');
    const trigger = document.getElementById('workspace-switcher-trigger');
    const chevron = document.getElementById('workspace-switcher-chevron');
    const popover = document.getElementById('workspace-popover');

    // Only reveal the brand-switcher chevron when there's actually more than
    // one brand to switch between. Tier-based "you could add more" hints
    // belong on the upgrade screen, not on every page-load lockup.
    if (switcher && trigger && popover && brands.length > 1) {
      // Keep the hidden <select> in sync with the active brand for any code
      // that reads its value.
      switcher.innerHTML = brands.map(b =>
        `<option value="${escapeHtml(b.id)}" ${b.id === activeBrand ? 'selected' : ''}>${escapeHtml(b.name)}</option>`
      ).join('');
      switcher.value = activeBrand;

      // Reveal the chevron — signals "this lockup is interactive".
      if (chevron) chevron.hidden = false;
      // Phase 35-C: explicit tooltip so hover discoverability isn't only visual.
      trigger.setAttribute('title', 'Switch workspace');
      trigger.setAttribute('aria-label', 'Switch workspace');
      // Phase 35-F: one-time coach mark for first-time multi-brand users.
      // localStorage-gated so it only ever shows once. Dismisses on
      // any interaction with the trigger or click anywhere else.
      try {
        const COACH_KEY = 'heist.workspaceSwitcherCoach.v1';
        if (localStorage.getItem(COACH_KEY) !== '1') {
          const coach = document.createElement('div');
          coach.className = 'workspace-coach';
          coach.setAttribute('role', 'tooltip');
          coach.innerHTML = `
            <div class="workspace-coach__arrow" aria-hidden="true"></div>
            <div class="workspace-coach__title">Switch workspace</div>
            <div class="workspace-coach__body">Click your workspace name to switch between brands or add a new one. Press <kbd>⌘K</kbd> and search "switch" anywhere in Heist.</div>
            <button class="workspace-coach__dismiss" type="button" aria-label="Dismiss">Got it</button>
          `;
          // Anchor inside the .sidebar-workspace row (which is positioned
          // sticky) so the coach floats just below the lockup.
          const wsRow = document.querySelector('.sidebar-workspace');
          if (wsRow) {
            wsRow.appendChild(coach);
            const dismiss = () => {
              try { localStorage.setItem(COACH_KEY, '1'); } catch (_) {}
              coach.remove();
              document.removeEventListener('click', maybeDismiss, true);
            };
            const maybeDismiss = (e) => {
              // Dismiss if user clicks the trigger (intentional interaction)
              // or if they click anything outside the coach itself.
              if (e.target.closest('#workspace-switcher-trigger')) { dismiss(); return; }
              if (e.target.closest('.workspace-coach')) return;
              dismiss();
            };
            coach.querySelector('.workspace-coach__dismiss')?.addEventListener('click', dismiss);
            // Slight delay so the document click that may have just opened
            // the page doesn't immediately dismiss it.
            setTimeout(() => document.addEventListener('click', maybeDismiss, true), 250);
          }
        }
      } catch (_) { /* localStorage unavailable — skip coach silently */ }

      // Render popover content: brand list + (multi-brand-tier only) divider + "+ New Brand".
      const itemsHtml = brands.map(b => `
        <button class="workspace-popover-item ${b.id === activeBrand ? 'is-active' : ''}" type="button" role="menuitem" data-brand-id="${escapeHtml(b.id)}">
          <span class="workspace-popover-item__name">${escapeHtml(b.name)}</span>
          ${b.id === activeBrand ? '<svg class="workspace-popover-item__check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>' : ''}
        </button>
      `).join('');
      const newBrandHtml = isMultiBrandTier ? `
        <div class="workspace-popover-divider" role="separator"></div>
        <button class="workspace-popover-item workspace-popover-item--new" type="button" role="menuitem" data-brand-id="__new__">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          <span>New Brand</span>
        </button>
      ` : '';
      popover.innerHTML = itemsHtml + newBrandHtml;

      const closePopover = () => {
        popover.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      };
      const openPopover = () => {
        popover.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
      };
      const togglePopover = () => {
        if (popover.hidden) openPopover(); else closePopover();
      };

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        togglePopover();
      });

      popover.addEventListener('click', async (e) => {
        const item = e.target.closest('.workspace-popover-item');
        if (!item) return;
        const brandId = item.dataset.brandId;
        closePopover();
        if (brandId === '__new__') {
          const { showBrandWizard } = await import('./ui/brandWizard.js');
          showBrandWizard({ onComplete: () => window.location.reload() });
          return;
        }
        if (brandId === activeBrand) return; // no-op, already active
        await client.switchBrand(brandId);
        localStorage.setItem('heist_active_brand', brandId);
        window.location.reload();
      });

      // Click outside / Escape → close
      document.addEventListener('click', (e) => {
        if (popover.hidden) return;
        if (e.target.closest('#workspace-popover') || e.target.closest('#workspace-switcher-trigger')) return;
        closePopover();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !popover.hidden) closePopover();
      });
    }
  } catch(e) { /* Brand loading failed — continue with default */ }

  // Brain completeness score in sidebar
  updateBrainScore();
  // Setup-progress chip [N/5] on Command Center sidebar item
  updateSetupChip();

  // 2. Initialize App
  const router = createRouter({ root });
  initNavBindings(router);
  router.start();

  // Mode Pill
  updateModePill();

  // Help Chat Widget (Phase 28D — upgrade of Phase 6A)
  initHelpWidget();

  // Setup orchestrator: single sequencer for verify-email, setup wizard,
  // and coachmark tour. Owns the `#?setup=1` and `#?tour=1` deep links.
  try { bootSetupOrchestrator(); } catch(e) { console.warn('[BOOT] setup orchestrator failed:', e); }

  // window.__restartTour is exposed on window so command-palette entries,
  // debug commands, and the help drawer can fire tours without re-importing
  // the module.
  window.__restartTour = restartTour;

  // Phase 3d/e: TWO entry points to the help drawer (which contains the
  // AI prompt + "Tour this page" trigger + help-center link):
  //   1. The persistent "Tips" button in the top-bar (icon + label).
  //      Premium-product pattern — matches Stripe / Figma / Notion's
  //      always-visible global help affordance.
  //   2. The smaller "?" button next to each page title — contextual,
  //      stays near the page-header for users who look there.
  // Both routes call the same toggleHelpDrawer().
  document.addEventListener('click', (e) => {
    // Top-bar Tips button (data-action="open-tips")
    const tipsBtn = e.target.closest('[data-action="open-tips"]');
    if (tipsBtn) {
      e.preventDefault();
      try { toggleHelpDrawer(); } catch (err) { console.warn('[HELP] drawer toggle failed:', err); }
      return;
    }
    // Page-header "?" button. data-tour-playlist is preserved on the
    // button so tests/old code paths that target the attribute still
    // work — but the click action is the same drawer toggle.
    const headerBtn = e.target.closest('[data-tour-playlist]');
    if (!headerBtn) return;
    if (!headerBtn.dataset.tourPlaylist) return;
    if (!headerBtn.classList.contains('page-tour-btn')) return;
    e.preventDefault();
    try { toggleHelpDrawer(); } catch (err) { console.warn('[HELP] drawer toggle failed:', err); }
  });

  // Persistent help drawer (Phase 3, N2). Pinned to the right edge on every
  // authed page; collapsed = "?" tab, expanded = page-relevant MCP prompt,
  // tour trigger, and quick-links. Auto-opens once during the user's first
  // 7 days post-signup so new users discover it.
  try { initHelpDrawer(); } catch (err) { console.warn('[BOOT] help drawer init failed:', err); }

  // Account Popover (Phase 25B)
  initAccountPopover();

  // Notifications (Phase 27B)
  initNotifications();

  // Command Palette (Phase 28B)
  try { initCommandPalette(); } catch (e) { console.error('[BOOT] Command palette init failed:', e); }

  // Global: clicking anywhere on a datetime-local input opens the picker
  document.addEventListener('click', (e) => {
    const input = e.target.closest('input[type="datetime-local"], input[type="date"]');
    if (input) { try { input.showPicker(); } catch {} }
  });

  // Boot-readiness signal: the shell (sidebar, topbar bell) is static HTML
  // and visible well before this async handler finishes awaiting authMe()
  // and getBrands(), so clicks on globally-bound controls (notification
  // bell, command palette) are dropped until this point. Stamp the <html>
  // element so tests — and any future boot-gated UI — can wait for it.
  document.documentElement.setAttribute('data-boot-complete', '1');

  console.log("[BOOT] v3 started");

  // Trial expiry banner (Phase 35D)
  renderTrialBanner(window.__auth__);
  window.addEventListener('hashchange', () => renderTrialBanner(window.__auth__));

  // Email verification banner (G2)
  // Show the blocking modal once on app boot. After it's dismissed (Skip or Verify),
  // the persistent top-of-page reminder bar takes over — we do NOT re-show the modal
  // on every hashchange, and we never stack duplicate banners.
  renderVerifyBanner(window.__auth__);

  // Upgrade modal on tier limit hit (Phase 35D)
  window.addEventListener('heist:upgrade-needed', (e) => {
    const { error } = e.detail;
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay upgrade-modal-overlay';
    overlay.innerHTML = `
      <div class="upgrade-modal">
        <div class="upgrade-modal-head">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
          <h2>Time to upgrade</h2>
          <p>${escapeHtml(error) || 'You have reached the limit of your current plan.'}</p>
        </div>
        <div class="upgrade-modal-body">
          <a href="#settings" class="btn btn-primary" onclick="this.closest('.upgrade-modal-overlay').remove()">View Plans</a>
          <button class="btn btn-glass" onclick="this.closest('.upgrade-modal-overlay').remove()">Maybe Later</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
  });

  // Dev Mode (Phase 5)
  initDevMode();
});

// G30-C6: Brain completeness score in sidebar
async function updateBrainScore() {
  try {
    const data = await client.getMemory();
    if (!data?.profile) return;
    const { pct } = computeBrainScore(data.profile);

    const navItem = document.querySelector('.nav-item--brain');
    if (!navItem) return;

    // Use data attribute + CSS ::after to show score without affecting textContent (for E2E tests)
    navItem.setAttribute('data-brain-score', pct);
    navItem.title = `Brain completeness: ${pct}%`;
    navItem.classList.toggle('brain-score--high', pct >= 80);
    navItem.classList.toggle('brain-score--mid', pct >= 40 && pct < 80);
    navItem.classList.toggle('brain-score--low', pct < 40);
  } catch(e) {
    // Not logged in or no profile — skip silently
  }
}
// Expose for Brain page to call after save
window.__updateBrainScore = updateBrainScore;
// Update score when navigating back from Brain
window.addEventListener('hashchange', () => {
  if (location.hash !== '#memory') updateBrainScore();
});

// Setup-progress chip removed — the Getting Started bar on the Command
// Center is the single source of progress, the sidebar should stay clean.
// (Stub kept so any external caller of __updateSetupChip is a no-op.)
function updateSetupChip() {
  const navItem = document.querySelector('.nav-item--home');
  if (navItem) navItem.removeAttribute('data-setup-chip');
}
window.__updateSetupChip = updateSetupChip;

// Phase 35D: Trial expiry banner
function renderTrialBanner(user) {
  const existing = document.getElementById('trial-expiry-banner');
  if (existing) existing.remove();

  if (!user || !user.trial_expires_at || user.has_stripe) return;
  if (['admin', 'angel_admin', 'god_admin'].includes(user.role)) return;
  const daysLeft = Math.max(0, Math.ceil((new Date(user.trial_expires_at) - Date.now()) / 86400000));
  if (daysLeft > 3) return;

  const banner = document.createElement('div');
  banner.id = 'trial-expiry-banner';
  banner.className = 'trial-banner';
  banner.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
    <span>${daysLeft === 0 ? 'Your trial has expired.' : `Your trial expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}.`} Upgrade to keep your features.</span>
    <a href="#upgrade" class="trial-banner-btn">Choose a Plan</a>
    <button class="trial-banner-close" aria-label="Dismiss">&times;</button>
  `;
  banner.querySelector('.trial-banner-close').addEventListener('click', () => banner.remove());

  const main = document.getElementById('main-content');
  if (main) main.prepend(banner);
}

// G2: Email verification — non-blocking reminder bar.
// (Was a blocking modal; that hid the setup wizard for fresh signups, so
// new users saw the verify modal first instead of the wizard. Now the bar
// at the top of the page is the only auto chrome. The user explicitly
// opens the verification modal by clicking "Verify Now" on the bar.)
function renderVerifyBanner(user) {
  // Always tear down any existing instance before deciding whether to render.
  document.getElementById('verify-overlay')?.remove();
  document.querySelector('.verify-reminder-bar')?.remove();
  if (!user || user.email_verified !== false) return;
  return mountVerifyReminderBar(user);
}

/**
 * Open the verify-email modal — Send Code → enter Code → Verify.
 * User-initiated only (called from the "Verify Now" button on the
 * reminder bar). Boot / auth-state changes never trigger this directly.
 */
function openVerifyModal(user) {
  document.getElementById('verify-overlay')?.remove();
  document.querySelector('.verify-reminder-bar')?.remove();
  if (!user) return;

  const overlay = document.createElement('div');
  overlay.id = 'verify-overlay';
  overlay.className = 'verify-overlay';
  overlay.innerHTML = `
    <div class="verify-card">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
      <h2 class="verify-card__title">Verify your email</h2>
      <p class="verify-card__desc">We'll send a 6-digit code to <strong>${escapeHtml(user.email) || 'your email'}</strong>.</p>
      <div id="verify-step-send">
        <button class="btn-primary full-width" id="send-verify-btn">Send Verification Code</button>
        <button class="btn-ghost full-width" id="cancel-verify-btn" style="margin-top:10px;font-size:12px;">Cancel</button>
      </div>
      <div id="verify-step-code" style="display:none">
        <p class="verify-card__hint">Enter the 6-digit code sent to your email:</p>
        <input type="text" id="verify-code-input" class="verify-code-input" maxlength="6" placeholder="000000" autocomplete="one-time-code">
        <div class="verify-card__actions">
          <button class="btn-primary full-width" id="submit-verify-btn">Verify</button>
        </div>
        <button class="btn-ghost" id="resend-verify-btn" style="margin-top:12px;font-size:12px;">Resend code</button>
        <button class="btn-ghost full-width" id="cancel-verify-code-btn" style="margin-top:10px;font-size:12px;">I'll do this later</button>
      </div>
      <p id="verify-error" class="verify-card__error" style="display:none"></p>
    </div>
  `;
  document.body.appendChild(overlay);

  const stepSend = overlay.querySelector('#verify-step-send');
  const stepCode = overlay.querySelector('#verify-step-code');
  const errorEl = overlay.querySelector('#verify-error');

  function showError(msg) {
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
  }

  // Single dismiss path shared by every escape hatch (Cancel on either step,
  // Escape key, backdrop click). Without this, a user who sent a code but
  // doesn't have it in hand was trapped on the code-entry step.
  function dismiss() {
    document.removeEventListener('keydown', onKeydown);
    overlay.remove();
    // Show the reminder bar again so the user can re-trigger later.
    mountVerifyReminderBar(user);
  }
  function onKeydown(e) {
    if (e.key === 'Escape') dismiss();
  }
  document.addEventListener('keydown', onKeydown);
  // Click on the dimmed backdrop (but not the card) dismisses.
  overlay.addEventListener('click', (e) => { if (e.target === overlay) dismiss(); });

  overlay.querySelector('#cancel-verify-btn')?.addEventListener('click', dismiss);
  overlay.querySelector('#cancel-verify-code-btn')?.addEventListener('click', dismiss);

  overlay.querySelector('#send-verify-btn')?.addEventListener('click', async () => {
    try {
      await client.sendVerification();
      stepSend.style.display = 'none';
      stepCode.style.display = 'block';
      errorEl.style.display = 'none';
    } catch (e) {
      showError('Failed to send code. Please try again.');
    }
  });

  overlay.querySelector('#resend-verify-btn')?.addEventListener('click', async () => {
    try {
      await client.sendVerification();
      errorEl.style.display = 'none';
    } catch (e) {
      showError('Failed to resend. Please try again.');
    }
  });

  overlay.querySelector('#submit-verify-btn')?.addEventListener('click', async () => {
    const code = overlay.querySelector('#verify-code-input')?.value?.trim();
    if (!code || code.length < 6) { showError('Please enter the 6-digit code.'); return; }
    try {
      await client.verifyEmail(code);
      document.removeEventListener('keydown', onKeydown);
      overlay.remove();
      document.querySelector('.verify-reminder-bar')?.remove();
      if (window.__auth__) window.__auth__.email_verified = true;
    } catch (e) {
      showError('Invalid code. Please check and try again.');
    }
  });

  // Allow Enter key on code input
  overlay.querySelector('#verify-code-input')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') overlay.querySelector('#submit-verify-btn')?.click();
  });
}

// Singleton top-of-page reminder bar shown after the modal is dismissed.
// Always tears down any existing bar first so navigation can never stack duplicates.
function mountVerifyReminderBar(user) {
  document.querySelector('.verify-reminder-bar')?.remove();
  const reminder = document.createElement('div');
  reminder.className = 'verify-reminder-bar';
  reminder.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg><span>Please verify your email to unlock all features.</span><button class="verify-reminder-btn" id="verify-now-btn">Verify Now</button>`;
  document.body.prepend(reminder);
  reminder.querySelector('#verify-now-btn')?.addEventListener('click', () => {
    // User explicitly clicked Verify Now — open the actual verification
    // modal (Send Code → enter code → Verify). The auto path stays as
    // the bar; this is the only way to fire the modal in the new flow.
    openVerifyModal(user);
  });
}

function timeAgo(dateStr) {
  const now = Date.now();
  const d = new Date(dateStr).getTime();
  const secs = Math.floor((now - d) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  const days = Math.floor(hrs / 24);
  if (days < 7) return days + 'd ago';
  return new Date(dateStr).toLocaleDateString();
}

const _NOTIF_ICON_PATHS = {
  'hand': '<path d="M18 11V6a2 2 0 0 0-4 0v1"/><path d="M14 10V4a2 2 0 0 0-4 0v2"/><path d="M10 10.5V6a2 2 0 0 0-4 0v8"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/>',
  'rocket': '<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>',
  'x-circle': '<circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>',
  'info': '<circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>',
  'bell': '<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>',
};
function notifIcon(name, w = 18, h = 18) {
  return `<svg width="${w}" height="${h}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">${_NOTIF_ICON_PATHS[name] || ''}</svg>`;
}

const NOTIF_TYPE_ICONS = {
  welcome: notifIcon('hand'),
  publish_success: notifIcon('rocket'),
  publish_fail: notifIcon('x-circle'),
  info: notifIcon('info'),
  tour_reminder: notifIcon('info'),
};

function initNotifications() {
  const bell = document.getElementById('notif-bell');
  const drawer = document.getElementById('notif-drawer');
  const backdrop = document.getElementById('notif-backdrop');
  const dot = document.getElementById('notif-dot');
  if (!bell || !drawer || !backdrop) return;

  let isOpen = false;

  async function fetchUnreadCount() {
    if (!window.__auth__) {
      if (dot) dot.hidden = true;
      return;
    }
    try {
      const { count } = await client.getUnreadCount();
      if (dot) dot.hidden = count === 0;
    } catch (e) {
      // Silently fail
    }
  }

  async function openDrawer() {
    if (!window.__auth__) return;
    isOpen = true;
    backdrop.hidden = false;
    drawer.classList.remove('open');
    drawer.offsetHeight; // force reflow for clean transition
    drawer.classList.add('open');

    // Show loading
    drawer.innerHTML = `
      <div class="notif-drawer-header">
        <span class="notif-drawer-title">Notifications</span>
        <div class="notif-drawer-actions">
          <button id="notif-mark-all" class="btn-ghost sm">Mark all read</button>
          <button id="notif-close" class="icon-btn" aria-label="Close" style="font-size:16px;">&times;</button>
        </div>
      </div>
      <div class="notif-list"><div class="notif-loading">Loading...</div></div>`;
    drawer.querySelector('#notif-close').addEventListener('click', closeDrawer);

    try {
      const { notifications } = await client.getNotifications();
      renderNotifications(notifications);
    } catch (e) {
      drawer.querySelector('.notif-list').innerHTML = `<div class="notif-empty"><div style="font-size:14px;">Failed to load notifications</div></div>`;
    }
  }

  function renderNotifications(notifications) {
    const list = drawer.querySelector('.notif-list');
    if (!list) return;

    if (!notifications || notifications.length === 0) {
      list.innerHTML = `
        <div class="notif-empty">
          <div style="margin-bottom:12px;opacity:0.4;">${notifIcon('bell', 32, 32)}</div>
          <div style="font-size:14px;font-weight:600;margin-bottom:4px;">No notifications</div>
          <div style="font-size:12px;">You're all caught up!</div>
        </div>`;
      return;
    }

    list.innerHTML = notifications.map(n => `
      <div class="notif-item ${n.read ? '' : 'notif-item--unread'}" data-id="${escapeHtml(n.id)}" ${n.link ? `data-link="${escapeHtml(n.link)}"` : ''}>
        <span class="notif-item__icon">${NOTIF_TYPE_ICONS[n.type] || notifIcon('info')}</span>
        <div class="notif-item__body">
          <div class="notif-item__title">${escapeHtml(n.title)}</div>
          ${n.body ? `<div class="notif-item__text">${escapeHtml(n.body)}</div>` : ''}
          <div class="notif-item__time">${timeAgo(n.created_at)}</div>
        </div>
        ${n.read ? '' : '<span class="notif-item__dot"></span>'}
        <button class="notif-item__delete" type="button" aria-label="Delete notification" title="Delete">&times;</button>
      </div>
    `).join('');

    // Click handlers for items
    list.querySelectorAll('.notif-item').forEach(item => {
      item.addEventListener('click', async (e) => {
        if (e.target.closest('.notif-item__delete')) return; // delete handled separately
        const id = item.dataset.id;
        const link = item.dataset.link;
        if (!item.classList.contains('notif-item--unread')) {
          if (link) { closeDrawer(); window.location.hash = link; }
          return;
        }
        try { await client.markNotificationRead(id); } catch (e) { /* ignore */ }
        item.classList.remove('notif-item--unread');
        const dotEl = item.querySelector('.notif-item__dot');
        if (dotEl) dotEl.remove();
        if (link) { closeDrawer(); window.location.hash = link; }
        fetchUnreadCount();
      });

      const delBtn = item.querySelector('.notif-item__delete');
      if (delBtn) {
        delBtn.addEventListener('click', async (e) => {
          e.stopPropagation();
          const id = item.dataset.id;
          try { await client.deleteNotification(id); } catch (_) { return; }
          item.remove();
          fetchUnreadCount();
          if (!list.querySelector('.notif-item')) renderNotifications([]);
        });
      }
    });

    // Mark all read
    const markAllBtn = drawer.querySelector('#notif-mark-all');
    if (markAllBtn) {
      markAllBtn.addEventListener('click', async () => {
        try {
          await client.markAllNotificationsRead();
          list.querySelectorAll('.notif-item--unread').forEach(el => {
            el.classList.remove('notif-item--unread');
            const d = el.querySelector('.notif-item__dot');
            if (d) d.remove();
          });
          fetchUnreadCount();
        } catch (e) { /* ignore */ }
      });
    }
  }

  function closeDrawer() {
    isOpen = false;
    drawer.classList.remove('open');
    backdrop.hidden = true;
  }

  bell.addEventListener('click', () => {
    if (isOpen) closeDrawer();
    else openDrawer();
  });
  backdrop.addEventListener('click', closeDrawer);

  // Initial check + polling
  fetchUnreadCount();
  setInterval(fetchUnreadCount, 60000);

  // Browser pop-up notifier — opt-in per personal_event. No-op until the
  // user creates a reminder with notify_browser_push and grants permission.
  startBrowserNotifier();
}

const HELP_FAQ = [
  { keys: ['brain', 'memory', 'brand', 'voice', 'identity'], answer: 'Head to <strong>The Brain</strong> tab to set up your brand identity — mission, audience, voice, and more. The AI uses this data to generate on-brand content.' },
  { keys: ['shortcut', 'keyboard', 'hotkey', 'cmd+k', 'ctrl+k'], answer: 'Key shortcuts: <strong>⌘K / Ctrl+K</strong> opens the command palette, <strong>Ctrl+Shift+D</strong> opens the dev panel, <strong>Escape</strong> closes panels.' },
  { keys: ['platform', 'twitter', 'instagram', 'facebook', 'linkedin', 'social'], answer: 'Heist supports <strong>X (Twitter), Instagram, Facebook, LinkedIn</strong>, and <strong>Blog</strong> (WordPress). Connect accounts in Settings → Integrations.' },
  { keys: ['price', 'pricing', 'plan', 'cost', 'trial', 'subscription', 'tier'], answer: 'Heist offers a 7-day free trial, then Starter ($19/mo), Pro ($49/mo), and Founder ($99/mo) plans. Check Settings for your current plan.' },
  { keys: ['start', 'begin', 'getting started', 'setup', 'new'], answer: 'Getting started? <strong>1)</strong> Set up your Brain, <strong>2)</strong> Create content in Content Studio, <strong>3)</strong> Review in Library, <strong>4)</strong> Schedule on the Calendar.' },
  { keys: ['campaign', 'campaigns', 'group', 'launch'], answer: 'Campaigns group related content together. Create one from the Campaigns page, add content items, and track them as a unit.' },
  { keys: ['library', 'content', 'posts', 'saved'], answer: 'Your <strong>Library</strong> stores all generated content. Filter by platform, status, or search. Edit, schedule, or delete from there.' },
  { keys: ['calendar', 'schedule', 'publish', 'auto'], answer: 'The <strong>Calendar</strong> shows your scheduled posts. Drag to reschedule, click to edit. Connect accounts in Settings to enable auto-publishing.' },
  { keys: ['analytics', 'stats', 'performance', 'followers'], answer: 'The <strong>Analytics</strong> page tracks your content performance and follower growth across connected platforms.' },
  { keys: ['generate', 'create', 'ai', 'write', 'content studio'], answer: 'Go to <strong>Content Studio</strong>, pick your platforms and tone, then click Generate. The AI creates drafts you can edit before saving.' },
  { keys: ['connect', 'integration', 'oauth', 'api key'], answer: 'Go to <strong>Settings → Integrations</strong> to connect your social accounts. For AI, check <strong>Settings → AI</strong> to configure your API key.' },
  { keys: ['import', 'paste', 'parse', 'manual', 'bulk'], answer: 'In <strong>Content Studio → Manual mode</strong>, use "Paste Your Content" to import existing posts. Paste your text, pick a platform, and click Import. Separate multiple posts with a blank line — each becomes its own draft.' },
  { keys: ['score', 'alignment', 'brand score', 'quality'], answer: 'Every generated post gets a <strong>brand alignment score</strong> (0-100) checking keyword usage, CTA presence, and avoid-word absence. Improve scores by completing your Brain profile fields.' },
  { keys: ['document', 'upload', 'pdf', 'vault', 'knowledge base'], answer: 'Upload reference documents (PDF, DOCX, TXT, CSV) in <strong>The Brain → Knowledge Base</strong>. The AI reads these to generate more accurate, on-brand content.' },
  { keys: ['blog', 'article', 'long-form', 'wordpress'], answer: 'Switch to <strong>Blog Post</strong> mode in Content Studio to generate long-form articles. Supports formatting, AI rewriting, and direct WordPress publishing.' },
  { keys: ['tone', 'tones', 'style', 'professional', 'casual'], answer: 'Choose from 8 tones in Content Studio: Professional, Casual, Witty, Bold, Inspirational, Educational, Provocative, and Storytelling. Or select "Other" to type a custom tone.' },
  { keys: ['template', 'templates', 'format'], answer: 'Content Studio offers social post templates to structure your content. Select a template from the dropdown before generating to guide the AI\'s output format.' },
  { keys: ['health', 'completeness', 'percentage'], answer: 'Your <strong>Brain health score</strong> (0-100%) measures how complete your brand profile is. Fill in all 7 identity fields plus add memory facts and documents to reach 100%.' },
  { keys: ['image', 'images', 'ai image', 'dall-e', 'dalle', 'imagen', 'generate image', 'picture'], answer: 'Click <strong>Generate with AI</strong> below any post\'s media zone, or open the Media Library → AI Generate tab. Describe the image, pick a size, and click Generate. Works with your existing OpenAI, Google, or Replicate key.' },
  { keys: ['video', 'ai video', 'replicate', 'generate video', 'clip'], answer: 'Open the Media Library → <strong>AI Generate</strong> tab → Generate Video section. Describe the video, pick an aspect ratio, and click Generate. Requires a <strong>Replicate API key</strong> — add it in Settings → AI Settings.' },
  { keys: ['ai media', 'bulk image', 'month image', 'media generation'], answer: 'Enable <strong>AI Images</strong> or <strong>AI Videos</strong> in the Generate My Month wizard or Campaign wizard to auto-generate media for every post in the batch.' },
];

function matchFaq(query) {
  const q = query.toLowerCase();
  for (const faq of HELP_FAQ) {
    if (faq.keys.some(k => q.includes(k))) return faq.answer;
  }
  return null;
}

function initHelpWidget() {
  const helpLauncher = document.getElementById('help-launcher');
  const helpPanel = document.getElementById('help-panel');
  const helpClose = document.getElementById('help-close');
  const helpInput = document.getElementById('help-input-field');
  const helpSend = document.getElementById('help-send-btn');
  const helpMessages = document.getElementById('help-chat-messages');

  if (!helpLauncher || !helpPanel) return;

  // ── help-widget context-aware layer ──
  // Reads window.__heistPageContext__ set by page mount() handlers and
  // swaps the welcome bubble + injects quick-action chips that deep-link
  // into the full Brain chat with a pre-filled prompt. Keeps the widget
  // lightweight (no LLM call) while making it feel smart.
  const PAGE_GREETINGS = {
    autopilot: {
      greeting: "You're on Autopilot. Want to discuss this week's topic, tweak settings, or chat with the Brain about what to post next?",
      chips: [
        { label: 'Pick next topic', prompt: 'Help me pick the next autopilot topic for this brand.' },
        { label: 'Pause a week', prompt: 'Pause the autopilot for the next 7 days.' },
        { label: 'Fresh topic ideas', prompt: 'Refresh the trending topic candidates for this brand.' },
      ],
    },
    calendar: {
      greeting: "Looking at your calendar. I can fill gaps, reschedule posts, or find your top-performing time slots.",
      chips: [
        { label: 'Fill this week', prompt: 'Fill this week with 3 posts per weekday for LinkedIn.' },
        { label: 'Best posting times', prompt: 'Show me the best posting times based on my engagement data.' },
      ],
    },
    library: {
      greeting: "In your library. Ask me to find something, generate variations, or clean up stale drafts.",
      chips: [
        { label: 'Top post this month', prompt: 'Find my top-performing post from the last 30 days.' },
        { label: 'Variations of a winner', prompt: 'Generate 5 variations of my top-performing post.' },
      ],
    },
    campaigns: {
      greeting: "Building campaigns. I can draft a full multi-post campaign from one prompt.",
      chips: [
        { label: 'Draft a campaign', prompt: 'Create a 12-post campaign for LinkedIn and Instagram about [topic].' },
        { label: 'Add variations', prompt: 'Generate 3 variations of this campaign.' },
      ],
    },
    'blog-editor': {
      greeting: "Writing a post. I can rewrite sections, add a FAQ, or generate a featured image.",
      chips: [
        { label: 'Rewrite a section', prompt: 'Rewrite the selected paragraph in a more conversational tone.' },
        { label: 'Add FAQ', prompt: 'Add a 3-question FAQ section to this blog post.' },
        { label: 'Generate image', prompt: 'Generate a featured image for this blog post.' },
      ],
    },
    memory: {
      greeting: "You're already in The Brain. The Chat tab has the full conversational assistant.",
      chips: [
        { label: 'Open Chat tab', prompt: '__nav:#memory', skipChat: true },
      ],
    },
  };

  const DEFAULT_GREETING = "Hi! I'm your Heist assistant. Ask me anything about features, content strategy, or how to get the most out of your Brain.";

  function getPageContext() {
    try { return window.__heistPageContext__ || null; } catch (_) { return null; }
  }

  function refreshHelpWidgetForContext() {
    if (!helpMessages) return;
    const ctx = getPageContext();
    const pageKey = ctx && ctx.page;
    const preset = pageKey && PAGE_GREETINGS[pageKey];

    // Remove any previous context welcome + chips we injected
    helpMessages.querySelectorAll('[data-context-welcome]').forEach(n => n.remove());
    const oldChips = helpPanel && helpPanel.querySelector('.help-widget-chips');
    if (oldChips) oldChips.remove();

    // Swap the first generic welcome with our context-specific one
    const firstBot = helpMessages.querySelector('.help-chat-bubble--bot');
    const greetText = preset ? preset.greeting : DEFAULT_GREETING;
    if (firstBot) {
      const contentEl = firstBot.querySelector('.help-chat-bubble__content');
      if (contentEl) contentEl.textContent = greetText;
      firstBot.setAttribute('data-context-welcome', '1');
    }

    // Inject chips row above the input area, if we have any
    if (preset && preset.chips && preset.chips.length) {
      const inputArea = helpPanel.querySelector('.help-widget-input-area');
      if (inputArea) {
        const row = document.createElement('div');
        row.className = 'help-widget-chips';
        preset.chips.forEach(c => {
          const btn = document.createElement('button');
          btn.className = 'help-widget-chip';
          btn.type = 'button';
          btn.textContent = c.label;
          btn.onclick = () => {
            // Nav-only chips (no chat seed)
            if (typeof c.prompt === 'string' && c.prompt.startsWith('__nav:')) {
              window.location.hash = c.prompt.slice(6);
              toggleHelp(true);
              return;
            }
            // Deep-link into the full Brain chat with the prompt seeded
            try {
              sessionStorage.setItem('brain.activeTab', 'chat');
              sessionStorage.setItem('brain.seedMessage', c.prompt);
            } catch (_) {}
            window.location.hash = '#memory';
            toggleHelp(true);
          };
          row.appendChild(btn);
        });
        inputArea.parentNode.insertBefore(row, inputArea);
      }
    }
  }

  const toggleHelp = (force) => {
    const isHidden = helpPanel.classList.toggle('hide', force);
    helpLauncher.setAttribute('aria-expanded', !isHidden);
    // Only show widget when authenticated
    if (!window.__auth__ && !isHidden) {
      helpPanel.classList.add('hide');
      return;
    }
    // Refresh page-aware greeting + chips each time we open
    if (!isHidden) {
      try { refreshHelpWidgetForContext(); } catch (e) { console.warn('[help-widget]', e); }
    }
  };

  // Hook the 'Full Brain Chat' footer link — activateChatTabOnFooterClick
  const footerChatLink = helpPanel.querySelector('.help-widget-footer a[href="#memory"]');
  if (footerChatLink) {
    footerChatLink.addEventListener('click', () => {
      try { sessionStorage.setItem('brain.activeTab', 'chat'); } catch(_) {}
      toggleHelp(true);
    });
  }

  // 'Report a Problem' footer button — opens the feedback modal pre-set to bug
  const reportBtn = document.getElementById('help-report-problem');
  if (reportBtn) {
    reportBtn.addEventListener('click', () => {
      toggleHelp(true);
      openFeedbackModal({ category: 'bug' });
    });
  }

  helpLauncher.onclick = (e) => {
    e.stopPropagation();
    toggleHelp();
  };

  if (helpClose) {
    helpClose.onclick = () => toggleHelp(true);
  }

  document.addEventListener('click', (e) => {
    if (!helpPanel.contains(e.target) && e.target !== helpLauncher) {
      toggleHelp(true);
    }
  });

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !helpPanel.classList.contains('hide')) toggleHelp(true);
  });

  // Cmd+K / Ctrl+K toggles the Brain popout from anywhere in the app.
  // Skip when the user is typing in an input/textarea/editor so we don't
  // clobber expected shortcuts.
  window.addEventListener('keydown', (e) => {
    // cmd-k-help-toggle
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      const tag = (e.target && e.target.tagName) || '';
      if (['INPUT','TEXTAREA','SELECT'].includes(tag) || (e.target && e.target.isContentEditable)) return;
      e.preventDefault();
      toggleHelp();
    }
  });

  function addMessage(text, isUser, { trusted = false } = {}) {
    if (!helpMessages) return;
    const bubble = document.createElement('div');
    bubble.className = `help-chat-bubble help-chat-bubble--${isUser ? 'user' : 'bot'}`;
    const content = document.createElement('div');
    content.className = 'help-chat-bubble__content';
    if (isUser) {
      // User input — always use textContent to prevent self-XSS
      content.textContent = text;
    } else if (trusted) {
      // Developer-controlled static HTML (FAQ answers, error fallbacks)
      content.innerHTML = text;
    } else {
      // Dynamic content from API — escape before rendering
      content.innerHTML = escapeHtml(text);
    }
    bubble.appendChild(content);
    helpMessages.appendChild(bubble);
    helpMessages.scrollTop = helpMessages.scrollHeight;
  }

  async function handleSend() {
    if (!helpInput) return;
    const q = helpInput.value.trim();
    if (!q) return;

    addMessage(q, true);
    helpInput.value = '';

    const answer = matchFaq(q);
    if (answer) {
      addMessage(answer, false, { trusted: true });
    } else {
      // No FAQ match — use AI Brain for intelligent response
      addMessage('<span style="color:var(--text-muted)">Thinking...</span>', false, { trusted: true });
      try {
        // Resolve the FAB's thread once per session, then persist its ID
        // in localStorage so the conversation continues across reloads
        // and across pages — Claude.ai-style.
        if (!window.__helpThreadId) {
          let stored = null;
          try { stored = localStorage.getItem('heist.helpThread.id'); } catch (_) {}
          if (stored) {
            window.__helpThreadId = stored;
          } else {
            const threadsRes = await client.getChatThreads();
            const supportThread = (threadsRes.data || threadsRes.threads || []).find(t => t.title === '__heist_support__');
            if (supportThread) {
              window.__helpThreadId = supportThread.id;
            } else {
              const newThread = await client.createChatThread({ title: '__heist_support__' });
              window.__helpThreadId = newThread.id;
            }
            try { localStorage.setItem('heist.helpThread.id', window.__helpThreadId); } catch (_) {}
          }
        }
        const res = await client.chatSend(window.__helpThreadId, {
          message: q,
          sessionContext: 'The user is asking for help using the Heist content marketing platform. You have access to Heist documentation in your context — answer accurately based on that documentation. Keep answers concise (2-3 sentences). If the question is about the user\'s brand or content strategy, suggest they use Chat with Brain (The Brain tab) for brand-specific questions. If unsure, suggest visiting the Help page (#help) or emailing support@heistbrain.com.'
        });
        // Remove "Thinking..." and add real answer
        const lastBubble = helpMessages?.lastElementChild;
        if (lastBubble) lastBubble.remove();
        const aiContent = res.ai?.content;
        if (aiContent) {
          addMessage(aiContent, false); // untrusted AI content — escapeHtml applied by addMessage
        } else {
          addMessage('Sorry, I couldn\'t process that. Try the <a href="#help">Help page</a>.', false, { trusted: true });
        }
      } catch(e) {
        const lastBubble = helpMessages?.lastElementChild;
        if (lastBubble) lastBubble.remove();
        addMessage('I\'m having trouble connecting. Try the <a href="#help">Help page</a> or email <a href="mailto:support@heistbrain.com">support@heistbrain.com</a>.', false, { trusted: true });
      }
    }
  }

  if (helpSend) {
    helpSend.addEventListener('click', handleSend);
  }
  if (helpInput) {
    helpInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); handleSend(); }
    });
  }
}

function initAccountPopover() {
  const avatar = document.getElementById('topbar-avatar');
  const popover = document.getElementById('account-popover');
  if (!avatar || !popover) return;

  function openPopover() {
    updatePopover(window.__auth__);
    // Refresh the AI-mode chip every time the popover opens so credits-
    // remaining stays current (boot-only refresh would go stale fast).
    try { updateModePill(); } catch (_) {}
    popover.hidden = false;
    avatar.setAttribute('aria-expanded', 'true');
  }

  function closePopover() {
    popover.hidden = true;
    avatar.setAttribute('aria-expanded', 'false');
  }

  function togglePopover() {
    if (popover.hidden) openPopover();
    else closePopover();
  }

  avatar.addEventListener('click', (e) => {
    e.stopPropagation();
    togglePopover();
  });

  // Outside click closes popover
  document.addEventListener('click', (e) => {
    if (!popover.hidden && !popover.contains(e.target) && e.target !== avatar) {
      closePopover();
    }
  });

  // Escape closes popover
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !popover.hidden) {
      closePopover();
    }
  });

  // My Profile link
  const profileLink = document.getElementById('popover-profile-link');
  if (profileLink) {
    profileLink.addEventListener('click', () => closePopover());
  }

  // Theme toggle
  const themeBtn = document.getElementById('popover-theme-toggle');
  if (themeBtn) {
    themeBtn.addEventListener('click', () => {
      if (window.__toggleTheme) window.__toggleTheme();
      // Update label after toggle
      const label = document.getElementById('popover-theme-label');
      if (label) {
        const theme = window.__getTheme ? window.__getTheme() : 'dark';
        label.textContent = theme === 'dark' ? 'Switch to Light' : 'Switch to Dark';
      }
    });
  }

  // Send Feedback
  const feedbackBtn = document.getElementById('popover-feedback');
  if (feedbackBtn) {
    feedbackBtn.addEventListener('click', () => {
      closePopover();
      openFeedbackModal();
    });
  }

  // Sign Out
  const signOutBtn = document.getElementById('popover-sign-out');
  if (signOutBtn) {
    signOutBtn.addEventListener('click', async () => {
      closePopover();
      try {
        await client.authLogout();
      } catch (e) { /* ignore */ }
      // Route through updateGlobalAuthState so all dependent surfaces
      // (verify banner, impersonation banner, tier badge, popover, etc.)
      // tear themselves down — not just window.__auth__.
      if (typeof window.updateGlobalAuthState === 'function') {
        window.updateGlobalAuthState(null, 'SIGN_OUT');
      } else {
        window.__auth__ = null;
      }
      window.location.hash = '#login';
    });
  }

  // Sidebar avatar → open settings
  const sidebarAvatar = document.getElementById('sidebar-avatar');
  if (sidebarAvatar) {
    sidebarAvatar.style.cursor = 'pointer';
    sidebarAvatar.addEventListener('click', () => { window.location.hash = '#settings'; });
  }
}

async function updateModePill() {
  const pill = document.getElementById("ai-mode-pill");
  const popoverChip = document.getElementById("popover-ai-mode");
  const popoverLabel = document.getElementById("popover-ai-mode-label");

  const isStubForced = localStorage.getItem('cp_dev_force_stub') === '1';
  const isDevOpen = !!document.getElementById('dev-panel-overlay');

  // Helper: write a human-readable AI-mode line into the avatar popover.
  // Tone is plain English ("Trial credits: 3 left") rather than the
  // dev-panel pill ("STUB / REAL / REAL (NO KEY)"). Dot color matches the
  // urgency: green = healthy, amber = needs attention, red = stubbed/broken.
  function writePopoverChip(label, dotColor) {
    if (!popoverChip || !popoverLabel) return;
    popoverLabel.textContent = label;
    popoverChip.style.setProperty('--popover-ai-mode-dot', dotColor);
    popoverChip.hidden = false;
  }

  try {
    const health = await client.healthAi();

    if (pill) {
      if (health.forceStub || isStubForced) {
        pill.textContent = isStubForced ? "STUB (FORCED)" : "STUB";
        pill.className = "mode-pill mode-pill--stub";
      } else if (health.openaiConfigured) {
        pill.textContent = "REAL";
        pill.className = "mode-pill mode-pill--real";
      } else {
        pill.textContent = "REAL (NO KEY)";
        pill.className = "mode-pill mode-pill--warn";
      }
      // Pill is dev-only — show only when the dev panel is open.
      pill.style.display = isDevOpen ? 'inline-flex' : 'none';
    }

    // Popover chip — human-readable, always shown when authed.
    const u = window.__auth__ || {};
    if (health.forceStub || isStubForced) {
      writePopoverChip(isStubForced ? 'AI: demo mode (forced)' : 'AI: demo mode', '#EF4444');
    } else if (u.has_ai_key) {
      const provider = (health.effectiveKeySource === 'user_byok' && health.provider) ? health.provider : null;
      writePopoverChip(provider ? `AI: using your ${capitalize(provider)} key` : 'AI: using your key', '#10B981');
    } else if (typeof u.free_generations_remaining === 'number' && u.free_generations_remaining > 0) {
      const left = u.free_generations_remaining;
      writePopoverChip(`AI: ${left} trial credit${left === 1 ? '' : 's'} left`, left <= 2 ? '#F59E0B' : '#A78BFA');
    } else if (typeof u.free_generations_remaining === 'number') {
      writePopoverChip('AI: trial credits exhausted — add a key', '#F59E0B');
    } else {
      writePopoverChip('AI: not configured', '#F59E0B');
    }
  } catch (err) {
    if (pill) pill.style.display = 'none';
    if (popoverChip) popoverChip.hidden = true;
  }
}

function capitalize(s) {
  if (!s || typeof s !== 'string') return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function initDevMode() {
  const panel = new DevPanel();

  window.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && e.code === 'KeyD') {
      e.preventDefault();
      panel.toggle();
    }
    if (e.key === 'Escape' && panel.isOpen) {
      panel.toggle();
    }
  });

  if (window.location.search.includes('dev=1')) {
    panel.toggle();
  }
}

class DevPanel {
  constructor() {
    this.isOpen = false;
    this.forceStub = localStorage.getItem('cp_dev_force_stub') === '1';
    this.aiContext = null;
  }

  async toggle() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      await this.refresh();
      document.body.classList.add('dev-mode-on');
    } else {
      document.body.classList.remove('dev-mode-on');
    }
    this.render();
  }

  async refresh() {
    try {
      this.aiContext = await client.getAiDebugContext();
    } catch (e) {
      console.error("[DEV_PANEL] Refresh failed:", e);
    }
  }

  setForceStub(val) {
    this.forceStub = val;
    localStorage.setItem('cp_dev_force_stub', val ? '1' : '0');
    updateModePill();
    this.render();
  }

  render() {
    const root = document.getElementById('overlays');
    if (!root) return;

    let overlay = document.getElementById('dev-panel-overlay');
    if (!this.isOpen) {
      if (overlay) overlay.remove();
      return;
    }

    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'dev-panel-overlay';
      overlay.className = 'dev-panel-overlay';
      // Close on outside click
      overlay.onclick = (e) => {
        if (e.target === overlay) this.toggle();
      };
      root.appendChild(overlay);
    }

    const ctx = this.aiContext || {};

    overlay.innerHTML = `
      <div class="dev-panel" style="pointer-events: auto;">
        <div class="dev-panel-header">
          <h3>Developer Platform</h3>
          <button id="dev-close" class="btn-ghost sm" aria-label="Close" style="padding: 4px 8px;"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
        </div>
        <div class="dev-panel-body">
          <div class="dev-section">
            <h4>AI Configuration</h4>
            <div class="dev-toggle-row">
              <span>Force STUB Mode (Header)</span>
              <input type="checkbox" id="dev-force-stub" ${this.forceStub ? 'checked' : ''}>
            </div>
            <div class="dev-info-grid">
              <span class="dev-info-label">Provider:</span>
              <span class="dev-info-value">${escapeHtml(ctx.provider || '...')}</span>
              <span class="dev-info-label">Mode:</span>
              <span class="dev-info-value">${escapeHtml(ctx.mode || '...')}</span>
              <span class="dev-info-label">Key Source:</span>
              <span class="dev-info-value">${escapeHtml(ctx.effectiveKeySource || '...')}</span>
              <span class="dev-info-label">Current:</span>
              <span class="dev-info-value">${this.forceStub ? 'STUB' : 'LIVE'}</span>
            </div>
          </div>
          
          <div class="dev-section">
            <h4>Environment Info</h4>
            <div class="dev-info-grid">
              <span class="dev-info-label">User ID:</span>
              <span class="dev-info-value">${escapeHtml(ctx.userId || '...')}</span>
              <span class="dev-info-label">Host:</span>
              <span class="dev-info-value">${window.location.hostname}</span>
              <span class="dev-info-label">Theme Class:</span>
              <span class="dev-info-value">${document.documentElement.className}</span>
            </div>
          </div>
        </div>
        <div class="dev-panel-footer">
          <button class="btn-ghost sm" id="dev-refresh" style="margin-right: 8px;">Refresh</button>
          <button class="btn-primary sm" id="dev-reload">Reload UI</button>
        </div>
      </div>
    `;

    overlay.querySelector('#dev-close').onclick = () => this.toggle();
    overlay.querySelector('#dev-refresh').onclick = () => this.refresh().then(() => this.render());
    overlay.querySelector('#dev-force-stub').onchange = (e) => this.setForceStub(e.target.checked);
    overlay.querySelector('#dev-reload').onclick = () => window.location.reload();
  }
}
